import { analyticsService } from '@meiuca/analytics';
import { ANALYTICS_EVENTS, SCREEN_NAMES } from '@/constants/analyticsEvents'
import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { H4, Body } from '@meiuca/ui-components'
import { ReservationCard } from '@/components/ReservationCard'
import { reservationService, type AppReservation, type PaginationInfo } from '@/services/reservationService'
import { Calendar, Loader2 } from 'lucide-react'
import { MarketplaceHeader } from '@/components/MarketplaceHeader'
import { LoginRequiredEmptyState } from '@/components/LoginRequiredEmptyState'
import { useAuth } from '@/contexts/AuthContext'

type TabType = 'open' | 'completed'

const buildReservationParams = (
  activeTab: TabType,
  cursor?: string
): { period: 'upcoming' | 'past'; limit: number; cursor?: string } => ({
  period: activeTab === 'open' ? 'upcoming' : 'past',
  limit: 20,
  ...(cursor ? { cursor } : {})
})

export function ReservationsPage() {
  const navigate = useNavigate()
  const { isAuthenticated, isLoading: isAuthLoading } = useAuth()
  useEffect(() => {
    analyticsService.trackScreenView(SCREEN_NAMES.RESERVATIONS);
  }, []);
  const [activeTab, setActiveTab] = useState<TabType>('open')
  const [reservations, setReservations] = useState<AppReservation[]>([])
  const [pagination, setPagination] = useState<PaginationInfo | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    // Only hit the (authenticated) reservations endpoint when logged in —
    // otherwise show the login empty state instead of a 401 error.
    if (isAuthenticated) {
      loadReservations()
    }
  }, [activeTab, isAuthenticated])

  const loadReservations = async () => {
    try {
      setLoading(true)
      setError(null)

      const params = buildReservationParams(activeTab)
      const response = await reservationService.listMyReservations(params)
      setReservations(response.data)
      setPagination(response.pagination)
    } catch (err) {
      console.error('Error loading reservations:', err)
      setError('Erro ao carregar suas reservas. Tente novamente.')
    } finally {
      setLoading(false)
    }
  }

  const loadMore = async () => {
    if (!pagination?.hasMore || loadingMore) return

    try {
      setLoadingMore(true)
      setError(null)

      const params = buildReservationParams(activeTab, pagination.cursor ?? undefined)
      const response = await reservationService.listMyReservations(params)
      setReservations(prev => [...prev, ...response.data])
      setPagination(response.pagination)
    } catch (err) {
      console.error('Error loading more reservations:', err)
      setError('Erro ao carregar mais reservas. Tente novamente.')
    } finally {
      setLoadingMore(false)
    }
  }

  return (
    <div className="min-h-screen bg-background-primary">
      <MarketplaceHeader />
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Header */}
        <div className="mb-6">
          <H4 className="text-text-primary font-semibold mb-2">
            Minhas Reservas
          </H4>
          <Body className="text-text-secondary">
            Acompanhe suas reservas ativas e histórico
          </Body>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-border-light">
          <button
            onClick={() => {
              analyticsService.track(ANALYTICS_EVENTS.RESERVATIONS_TAB_CHANGED, { tab: 'open' })
              setActiveTab('open')
            }}
            className={`px-4 py-2 text-sm font-medium transition-colors ${
              activeTab === 'open'
                ? 'text-brand-primary border-b-2 border-brand-primary'
                : 'text-text-secondary hover:text-text-primary'
            }`}
          >
            Abertas
          </button>
          <button
            onClick={() => {
              analyticsService.track(ANALYTICS_EVENTS.RESERVATIONS_TAB_CHANGED, { tab: 'completed' })
              setActiveTab('completed')
            }}
            className={`px-4 py-2 text-sm font-medium transition-colors ${
              activeTab === 'completed'
                ? 'text-brand-primary border-b-2 border-brand-primary'
                : 'text-text-secondary hover:text-text-primary'
            }`}
          >
            Concluídas
          </button>
        </div>

        {/* Content */}
        {isAuthLoading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <Loader2 className="w-10 h-10 text-brand-primary animate-spin" />
          </div>
        ) : !isAuthenticated ? (
          <LoginRequiredEmptyState
            title="Entre para ver suas reservas"
            description="Faça login para acompanhar suas reservas ativas e seu histórico."
          />
        ) : loading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <Loader2 className="w-10 h-10 text-brand-primary animate-spin" />
            <Body className="text-text-secondary mt-4">Carregando reservas...</Body>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-12">
            <Body className="text-feedback-error mb-4">{error}</Body>
            <button
              onClick={loadReservations}
              className="px-4 py-2 bg-brand-primary text-white rounded-lg hover:bg-brand-primary/90 transition-colors"
            >
              Tentar novamente
            </button>
          </div>
        ) : reservations.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="w-16 h-16 mb-4 rounded-full bg-background-secondary flex items-center justify-center">
              <Calendar className="w-8 h-8 text-text-tertiary" />
            </div>
            <H4 className="text-text-primary mb-2">
              Nenhuma reserva encontrada
            </H4>
            <Body className="text-text-secondary text-center max-w-md">
              {activeTab === 'open'
                ? 'Você ainda não tem reservas ativas. Explore as arenas disponíveis e faça sua primeira reserva!'
                : 'Você ainda não tem histórico de reservas.'}
            </Body>
          </div>
          ) : (
          <div className="space-y-4">
            {reservations.map((reservation, index) => (
              <ReservationCard
                key={`${reservation.id}-${index}`}
                reservation={reservation}
                position={index}
                onClick={() => {
                  analyticsService.track(ANALYTICS_EVENTS.RESERVATION_CARD_CLICKED, { reservation_id: reservation.id, position: index })
                  navigate(`/reservations/${reservation.id}`)
                }}
              />
            ))}

            {/* Load More Button */}
            {pagination?.hasMore && (
              <div className="flex justify-center pt-4">
                <button
                  onClick={loadMore}
                  disabled={loadingMore}
                  className="px-6 py-3 bg-brand-primary text-white rounded-lg hover:bg-brand-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {loadingMore ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Carregando...
                    </>
                  ) : (
                    'Carregar mais'
                  )}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
